'use client';

import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useScrollProgress } from '@eliadmin/core/src/components/dlz/ui/scroll-progress';
import { SearchField } from '@eliadmin/core/src/components/dlz/home/stores/search-field';
import {
  fetchAllStores,
  fetchStoresPage,
  type Store,
} from '@eliadmin/core/src/components/dlz/home/stores/stores-source';
import { matchesQuery } from '@eliadmin/core/src/components/dlz/home/stores/text-match';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/*
 * No Maps API key exists, so the map is the keyless Google Maps embed of the pickup point's
 * coordinates, loaded lazily; the arrow opens the same place in Google Maps (no key either).
 */
const embedUrl = (query: string, locale: string) =>
  `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=15&hl=${locale}&output=embed`;
const mapsUrl = (query: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

export function StoresFinder({
  heading,
  searchLabel,
  initialStores,
  initialHasMore,
  locale,
}: {
  heading: ReactNode;
  searchLabel: string;
  /** First page of the list; the next pages load while the list is scrolled. */
  initialStores: Store[];
  initialHasMore: boolean;
  locale: string;
}) {
  const { t } = useTranslations();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(initialStores[0].id);
  const [stores, setStores] = useState(initialStores);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [loadingMore, setLoadingMore] = useState(false);
  // The search matches names and addresses, so it needs every pickup point – loaded on the first query.
  const [allStores, setAllStores] = useState<Store[] | null>(null);
  const searching = query.trim() !== '';
  const loadingAll = searching && !allStores;

  const pool = searching && allStores ? allStores : stores;
  const visible = searching
    ? pool.filter(store => matchesQuery(query, store.name, ...store.lines))
    : pool;
  // The map follows the search: when the chosen store is filtered out, the first hit is shown.
  const active =
    visible.find(store => store.id === selectedId) ??
    visible[0] ??
    pool.find(store => store.id === selectedId) ??
    stores[0];
  const scroll = useScrollProgress<HTMLUListElement>('y', `${query}|${visible.length}`);

  const nextPage = useRef(2);
  const requested = useRef(false);
  const sentinel = useRef<HTMLLIElement>(null);
  const loadMore = hasMore && !searching;

  // Scrolling to the end of the list fetches the next page.
  useEffect(() => {
    const root = scroll.ref.current;
    const target = sentinel.current;
    if (!loadMore || !root || !target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || requested.current) return;
        requested.current = true;
        setLoadingMore(true);
        fetchStoresPage(locale, nextPage.current)
          .then(result => {
            nextPage.current += 1;
            setStores(current => {
              const known = new Set(current.map(store => store.id));
              return [...current, ...result.stores.filter(store => !known.has(store.id))];
            });
            setHasMore(result.hasMore);
          })
          // A failed request stops the loading instead of retrying in a loop.
          .catch(() => setHasMore(false))
          .finally(() => {
            requested.current = false;
            setLoadingMore(false);
          });
      },
      { root, rootMargin: '0px 0px 160px 0px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [loadMore, locale, stores.length, scroll.ref]);

  // A new query starts at the top of the list (the browsed list may be scrolled far down).
  useEffect(() => {
    scroll.ref.current?.scrollTo({ top: 0 });
  }, [query, scroll.ref]);

  useEffect(() => {
    if (!searching || allStores) return;
    let current = true;
    const controller = new AbortController();
    fetchAllStores(locale, controller.signal)
      .then(result => current && setAllStores(result))
      // Without the full list the search keeps matching the stores loaded so far.
      .catch(() => current && setAllStores(stores));
    return () => {
      current = false;
      controller.abort();
    };
    // `stores` only seeds the fallback; the request runs once per first query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching, allStores, locale]);

  return (
    <div className="flex flex-col gap-10 lg:flex-row">
      <div className="flex flex-col gap-6 lg:h-[564px] lg:w-[427px] lg:shrink-0">
        {heading}
        <SearchField label={searchLabel} value={query} onChange={setQuery} />
        {visible.length || loadingAll ? (
          /*
           * The list scrolls in a fixed box: 257 px with the Figma scrollbar thumb 16 px right of the
           * items on phones, the rest of the column on desktop. Its 4 px padding keeps the focus
           * rings inside the scroll box.
           */
          <div className="relative flex min-h-0 flex-1 flex-col">
            <ul
              ref={scroll.ref}
              aria-busy={loadingMore || loadingAll}
              className="hide-scroll-bar -m-1 flex flex-col gap-4 overflow-y-auto p-1 max-lg:h-[265px] max-lg:pr-6 lg:min-h-0 lg:flex-1"
            >
              {visible.map(store => {
                const selected = store.id === active.id;
                return (
                  // Figma draws the divider inside the item (inner stroke), so it adds no height.
                  <li key={store.id} className="relative pb-4 shadow-[inset_0_-1px_0_#D5D8DC]">
                    <button
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setSelectedId(store.id)}
                      className={cn(
                        'flex w-full flex-col gap-2 rounded-[6px] pr-8 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2',
                        selected ? 'text-[#0F1012]' : 'text-[#6B7079] hover:text-[#0F1012]',
                      )}
                    >
                      <span className="dlz-body-m min-h-6 font-semibold">{store.name}</span>
                      <span className="dlz-body-m">
                        {store.lines.map(line => (
                          <span key={line} className="block">
                            {line}
                          </span>
                        ))}
                      </span>
                    </button>
                    <a
                      href={mapsUrl(store.map)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${t('text2375')}: ${store.name}`}
                      className={cn(
                        'absolute right-0 top-0 rounded-full transition-colors hover:text-[#D7191F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] max-lg:-m-2.5 max-lg:p-2.5',
                        selected ? 'text-[#D7191F]' : 'text-[#E2E4E7]',
                      )}
                    >
                      <Icon name="outward" size={24} />
                    </a>
                  </li>
                );
              })}
              {loadingAll && !visible.length ? (
                <li aria-hidden="true" className="h-8 shrink-0">
                  <span className="block h-2 animate-pulse rounded bg-[#E2E4E7]" />
                </li>
              ) : null}
              {loadMore ? (
                <li ref={sentinel} aria-hidden="true" className="h-8 shrink-0">
                  {loadingMore ? (
                    <span className="block h-2 animate-pulse rounded bg-[#E2E4E7]" />
                  ) : null}
                </li>
              ) : null}
            </ul>
            {scroll.overflow && scroll.thumb < 1 ? (
              <span
                aria-hidden="true"
                className="absolute right-0 w-1 rounded-sm bg-[#E2E4E7] lg:hidden"
                style={{
                  height: `${scroll.thumb * 100}%`,
                  top: `${scroll.position * (1 - scroll.thumb) * 100}%`,
                }}
              />
            ) : null}
          </div>
        ) : (
          <p className="dlz-body-m text-[#6B7079]">{t('text752')}</p>
        )}
      </div>
      <div className="relative h-[300px] overflow-hidden rounded-2xl bg-[#E2E4E7] md:h-[400px] lg:h-[564px] lg:flex-1">
        <iframe
          src={embedUrl(active.map, locale)}
          title={[active.name, ...active.lines].join(', ')}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="absolute inset-0 size-full border-0"
        />
      </div>
    </div>
  );
}
